import { nextTick } from 'vue';

import {
	DropdownDirections,
	type Position,
} from '@/dsComponents/Dropdown/types';

export const computePosition = async (
	clickArea: HTMLElement | undefined,
	dropdownArea: HTMLElement | undefined,
	direction: DropdownDirections
): Promise<Position | null> => {
	if (!clickArea) return null;
	const { top, left, right, height, width } = clickArea.getBoundingClientRect();
	const dropdownSpacing = 4;

	// biome-ignore lint/suspicious/noImplicitAnyLet: Init Biome
	let positionLeft, positionTop;

	switch (direction) {
		case DropdownDirections.RIGHT: {
			positionLeft = right + dropdownSpacing;
			positionTop = top - dropdownSpacing;
			break;
		}
		// "automatic" | "bottom"
		default: {
			positionLeft = left;
			positionTop = top + height + dropdownSpacing;
		}
	}

	if (!dropdownArea) return { top: positionTop, left: positionLeft };
	await nextTick();

	switch (direction) {
		case DropdownDirections.LEFT:
			positionLeft = left - (dropdownArea.offsetWidth + dropdownSpacing);
			positionTop = top - dropdownSpacing;
			break;
		case DropdownDirections.TOP:
		case DropdownDirections.TOP_LEFT:
			positionTop = top - (dropdownArea.offsetHeight + dropdownSpacing);
			break;
		case DropdownDirections.TOP_CENTER:
			positionLeft = left + width / 2 - dropdownArea.offsetWidth / 2;
			positionTop = top - (dropdownArea.offsetHeight + dropdownSpacing);
			break;
		case DropdownDirections.TOP_RIGHT:
			positionLeft = right - dropdownArea.offsetWidth;
			positionTop = top - (dropdownArea.offsetHeight + dropdownSpacing);
			break;
		case DropdownDirections.BOTTOM_CENTER:
			positionLeft = left + width / 2 - dropdownArea.offsetWidth / 2;
			break;
		case DropdownDirections.BOTTOM_RIGHT:
			positionLeft = right - dropdownArea.offsetWidth;
			break;
	}

	const xExceeds = dropdownArea.offsetWidth + positionLeft;
	const yExceeds = dropdownArea.offsetHeight + positionTop + dropdownSpacing;

	const isTopDirection = [
		DropdownDirections.TOP,
		DropdownDirections.TOP_LEFT,
		DropdownDirections.TOP_CENTER,
		DropdownDirections.TOP_RIGHT,
	].includes(direction);
	const alwaysFullyDisplayTop = isTopDirection
		? dropdownSpacing
		: window.innerHeight - dropdownArea.offsetHeight - dropdownSpacing;

	// Handle X axis overflow
	if (xExceeds > window.innerWidth) {
		positionLeft = left - dropdownArea.offsetWidth + width;
	} else if (positionLeft < 0) {
		positionLeft = left;
	}
	// Handle Y axis overflow
	if (yExceeds > window.innerHeight) {
		const newY = top - dropdownArea.offsetHeight - dropdownSpacing;
		positionTop = newY;
		if (newY <= 0) {
			positionTop = alwaysFullyDisplayTop;
		}
	} else if (positionTop < 0) {
		const newY = top + height + dropdownSpacing;
		positionTop = newY;
		if (dropdownArea.offsetHeight + newY > window.innerHeight) {
			positionTop = alwaysFullyDisplayTop;
		}
	}

	return { top: positionTop, left: positionLeft };
};
